if (!window.customElements.get('product-bar')) {
  window.customElements.define('product-bar', class ProductBar extends HTMLElement {
    selectors = {
      section: '[data-section-type="product"]',
      buyButtonsBlock: '[data-block-type="buy-buttons"]',
      variantSelectorBlock: '[data-block-type="variant-selector"]',
      variantPicker: 'variant-picker',
      addButton: '.product-form__add-button',
      priceList: '.price-list',
      barButton: '.product-bar__button',
      barPrice: '.product-bar__price',
      barIcon: 'svg, .icon',
    };

    connectedCallback() {
      /* Defer init — the bar is rendered before the product form in the DOM,
      so .product-form__payment-container doesn't exist yet at this point. */
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this._init(), { once: true });
      } else {
        this._init();
      }
    }

    _init() {
      this._formId = this.dataset.productFormId;
      this._form = document.forms[this._formId];

      this._setupScrollObserver();
      this._setupPriceObserver();
      this._setupButtonObserver();

      this._barBtn = this.querySelector(this.selectors.barButton);
      this._barBtn?.addEventListener('click', this._onBarClick.bind(this));
    }

    disconnectedCallback() {
      this._io?.disconnect();
      this._priceMo?.disconnect();
      this._btnMo?.disconnect();
    }

    _setupScrollObserver() {
      const section = this.closest(this.selectors.section);
      if (!section) return;

      /* Observe the stable wrapper, NOT .product-form__payment-container
         which gets replaced by product-rerender on every variant change. */
      const target = section.querySelector(this.selectors.buyButtonsBlock);
      if (!target) return;

      const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;

      this._io = new IntersectionObserver(([e]) => {
        /* Show only when the button has scrolled ABOVE the viewport */
        const scrolledPast = !e.isIntersecting && e.boundingClientRect.bottom < headerHeight;
        this.setAttribute('aria-hidden', scrolledPast ? 'false' : 'true');
      }, { rootMargin: `-${headerHeight}px 0px 0px 0px`, threshold: 0 });

      this._io.observe(target);
    }

    _setupPriceObserver() {
      const section = this.closest(this.selectors.section);
      if (!section) return;

      const container = section.querySelector(this.selectors.variantSelectorBlock);
      if (!container) return;

      this._syncPrice(container);

      this._priceMo = new MutationObserver(() => {
        this._syncPrice(section.querySelector(this.selectors.variantSelectorBlock));
      });

      this._priceMo.observe(container.parentElement, { childList: true, subtree: true });
    }

    _syncPrice(container) {
      if (!container) return;
      const priceList = container.querySelector(this.selectors.priceList);
      if (priceList) {
        const barPrice = this.querySelector(this.selectors.barPrice);
        if (barPrice) {
          barPrice.replaceChildren(priceList.cloneNode(true));
        }
      }
    }

    _setupButtonObserver() {
      const section = this.closest(this.selectors.section);
      if (!section) return;

      const container = section.querySelector(this.selectors.buyButtonsBlock);
      if (!container) return;

      this._syncButton(container);

      this._btnMo = new MutationObserver(() => {
        this._syncButton(section.querySelector(this.selectors.buyButtonsBlock));
      });

      this._btnMo.observe(container.parentElement, { childList: true, subtree: true });
    }

    _syncButton(container) {
      if (!container) return;
      const origBtn = container.querySelector(this.selectors.addButton);
      const barBtn = this.querySelector(this.selectors.barButton);
      if (!origBtn || !barBtn) return;

      barBtn.disabled = origBtn.disabled;
      barBtn.classList.toggle('button--disabled', origBtn.disabled);
      /* keep the cart icon if present */
      const icon = barBtn.querySelector(this.selectors.barIcon);
      const iconHtml = icon ? icon.outerHTML : '';
      barBtn.innerHTML = iconHtml + ' ' + origBtn.textContent.trim();
    }

    _onBarClick(e) {
      e.preventDefault();
      const section = this.closest(this.selectors.section);
      if (!section) return;

      /* Multi-variant products: scroll to the picker so the user can pick a
         variant. Forwarding the click would silently no-op when the add button
         is disabled (no variant chosen / sold-out default). */
      const picker = section.querySelector(this.selectors.variantPicker);
      if (picker) {
        const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
        const top = picker.getBoundingClientRect().top + window.pageYOffset - headerHeight - 16;
        window.scrollTo({ top, behavior: 'smooth' });
        return;
      }

      const origBtn = section.querySelector(`${this.selectors.buyButtonsBlock} ${this.selectors.addButton}`);
      if (origBtn && !origBtn.disabled) {
        origBtn.click();
      }
    }
  });
}
